import type { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import type { IGenericTableHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';
import type { IGenericTableRowFormatting } from '@/interfaces/powerMarkets/table/IGenericTableRowFormatting.ts';
import type { IGenericTableRowData } from '@/interfaces/powerMarkets/table/IGenericTableRowData.ts';
import type { TTableColumnType } from '@/types/table/TTableColumnType.ts';

const DEFAULT_ROW_FORMATTING: IGenericTableRowFormatting = {
    cellType: 'text',
    backgroundColour: null,
    cellFormatProperties: null,
    dateFormat: null,
    flash: false,
    flashCounter: 0,
    freeze: false,
    headerOverrides: null,
    hideEntry: false,
    percent: 0,
    percentColour: null,
    textColour: null,
    tooltip: null,
    valueToColours: null,
    valueToIcons: null,
    values: [],
};

export function createDefaultFilterProperty (cellType: TTableColumnType): IGenericTableRowFormatting {
    return {
        ...DEFAULT_ROW_FORMATTING,
        cellType,
    };
}

/**
 * Returns the first cell object found for a header across all rows.
 * Row 0 may omit sparse columns; filter metadata should come from any row that has the cell.
 */
export function getSampleCellForHeader (
    tableData: IGenericTableRowData[],
    headerId: string,
): IGenericTableRowFormatting | undefined {
    const rowWithCell = tableData.find(row => row.row?.[headerId]);
    return rowWithCell?.row[headerId];
}

export interface ITableFilterColumnFromData {
    headerId: string;
    title: string;
    filterHeader: IGenericTableHeaderRow;
    filterProperty: IGenericTableRowFormatting;
}

/**
 * Derives unique values for a table column from row data. Used by GenericTable when the API
 * doesn't provide header.uniqueValues, and by getTableFilterColumnsFromData.
 */
export function getUniqueValuesForHeader (
    tableData: IGenericTableRowData[],
    headerId: string,
    cellType: 'text' | 'number',
): Array<string> | Array<number> | undefined {
    const values = tableData.reduce<Set<string | number>>((set, row) => {
        const cell = row.row?.[headerId];
        if (!cell) return set;
        (cell.values ?? []).forEach((v) => {
            if (v != null && v !== '') set.add(typeof v === 'number' ? v : String(v));
        });
        return set;
    }, new Set<string | number>());
    const uniqueValues = Array.from(values).sort((a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))));
    if (uniqueValues.length === 0) return undefined;
    if (cellType === 'number') {
        return uniqueValues.filter((x): x is number => typeof x === 'number');
    }
    return uniqueValues.map(x => String(x));
}

/**
 * Builds filter column config from generic table data for use with GenericHeaderFilter.
 * Uses table headers and derives unique values from row data so any consumer (e.g. AddLeaderboardModal
 * or future table pre-filter UIs) shares the same logic as the table's filter behaviour.
 *
 * @param table - Table data (e.g. from leaderboard or other IGenericTableData API)
 * @param columnTitles - Header titles to include (order preserved; missing titles skipped)
 * @param numericTitles - Titles that should be treated as numeric for filter type/options
 */
export function getTableFilterColumnsFromData (
    table: IGenericTableData,
    columnTitles: string[],
    numericTitles: string[] = [],
): ITableFilterColumnFromData[] {
    const headerRows = table.headerSecondRow ?? Object.values(table.headerFirstRowById ?? {});
    const tableData = table.data ?? [];

    return columnTitles.reduce<ITableFilterColumnFromData[]>((columns, title) => {
        const header = headerRows.find((h: { title?: string; id?: string }) => h.title === title) as IGenericTableHeaderRow | undefined;
        if (!header?.id) return columns;

        const headerId = header.id;
        const sampleCell = getSampleCellForHeader(tableData, headerId);
        const isNumeric = numericTitles.includes(title);
        const cellType = sampleCell?.cellType ?? (isNumeric ? 'number' : 'text');
        const filterCellType: 'text' | 'number' = cellType === 'number' ? 'number' : 'text';
        const typedUniqueValues = getUniqueValuesForHeader(tableData, headerId, filterCellType);
        const filterProperty: IGenericTableRowFormatting = sampleCell ?? createDefaultFilterProperty(cellType);
        const filterHeader: IGenericTableHeaderRow = {
            ...header,
            uniqueValues: typedUniqueValues,
            allowSort: false,
        };
        columns.push({
            headerId,
            title,
            filterHeader,
            filterProperty,
        });
        return columns;
    }, []);
}
